import { MetricProto } from "build-ts/metadata";
import React, { useEffect } from "react";
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { MetricEdgeType } from "build-ts/enums";


interface MKGVisualizerProps {
  useMetricKnowledge?: MetricProto;
}


// Custom node component for the main metric node
const MainMetricNode = ({ data }: any) => {
  return (
    <div style={{
      background: "#481c64",
      border: "2px solid #481c64",
      color: "white",
      padding: "8px",
      borderRadius: "8px",
      fontSize: "20px",
      fontWeight: "arial",
      minWidth: "80px",
      textAlign: "center",
    }}>
      {data.label}
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ background:"#481c64", border: "2px solid white" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ background: "#481c64", border: "2px solid white" }}
      />
    </div>
  );
};


// Custom node component for connected nodes
const ConnectedNode = ({ data }: any) => {
  return (
    <div style={{
      background: "#3182ce",
      border: "2px solid #3182ce",
      color: "white",
      padding: "4px",
      borderRadius: "8px",
      fontSize: "20px",
      fontWeight: "arial",
      minWidth: "80px",
      textAlign: "center",
    }}>
      {data.label}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ background: "#3182ce", border: "2px solid white" }}
      />
    </div>
  );
};


const nodeTypes = {
  mainMetric: MainMetricNode,
  connected: ConnectedNode,
};


// Internal component that uses the hooks
const MKGVisualizerFlow: React.FC<MKGVisualizerProps> = ({
  useMetricKnowledge,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (!useMetricKnowledge) return;

    const nodesData: any[] = [];
    const edgesData: any[] = [];

    // Main Metric Node
    nodesData.push({
      id: useMetricKnowledge?.header?.id,
      type: "mainMetric",
      data: {
        label: useMetricKnowledge?.header?.name,
      },
      position: { x: 0, y: 0 },
    });

    useMetricKnowledge?.edges?.forEach((edge, index) => {
      switch (edge.edge_type) {
        case MetricEdgeType.METRIC_ATTRIBUTE_EDGE:
        case MetricEdgeType.METRIC_BENCHMARK_EDGE:
        case MetricEdgeType.METRIC_METRIC_EDGE:
        case MetricEdgeType.METRIC_MEASURE_EDGE:
        case MetricEdgeType.METRIC_TIMEFRAME_EDGE:
          // Position nodes based on which handle they connect to
          const xOffset = 800;
          const isTopConnection = index % 2 !== 0; // odd indices go to top
          const groupIndex = Math.floor(index / 2);
          const yOffset = isTopConnection
            ? -(groupIndex + 1) * 100  // Negative Y for top connections
            : (groupIndex + 1) * 100;  // Positive Y for bottom connections

          nodesData.push({
            id: edge.header?.id,
            type: "connected",
            data: {
              label: edge.header?.name,
            },
            position: { x: xOffset, y: yOffset },
          });
          
          // Alternate between top and bottom handles
          const sourceHandle = index % 2 === 0 ? "bottom" : "top";

          edgesData.push({
            id: `${useMetricKnowledge?.header?.id}-${edge.header?.id}`,
            source: useMetricKnowledge?.header?.id,
            target: edge.header?.id,
            sourceHandle: sourceHandle,
            targetHandle: "left",
            label: `${edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE ? "Benchmark" : edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE ? "Attribute" : edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE ? "Measure" : edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE ? "Timeframe" : edge.edge_type === MetricEdgeType.METRIC_METRIC_EDGE ? "Metric" : "UNKNOWN"}`,
            labelStyle: {
                fill: "#0ca5e9",
                fontSize: 18,
                fontFamily: "sans-serif",
                fontStyle: "normal",
            },
            labelBgStyle: {
                fill: "#ffffff",
                fillOpacity: 0.8,
                stroke: "#0ca5e9",
                strokeWidth: 0.5,
            },
            labelBgPadding: [2, 2],
            labelBgBorderRadius: 4,
            type: "curve",
            style: {
              stroke: "#3182ce",
              strokeWidth: 2,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#3182ce",
            },
          });
          break;
      }
    });

    setNodes(nodesData as any);
    setEdges(edgesData as any);
  }, [useMetricKnowledge]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      defaultViewport={{ x: 0, y: 0, zoom: 1 }}
      fitView={true}
      fitViewOptions={{ padding: 50 }}
      proOptions={{ hideAttribution: true }}
    >
      <Background />
    </ReactFlow>
  );
};

// Main component with its own ReactFlowProvider
const MKGVisualizer: React.FC<MKGVisualizerProps> = ({
  useMetricKnowledge,
}) => {
  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <ReactFlowProvider>
        <MKGVisualizerFlow useMetricKnowledge={useMetricKnowledge} />
      </ReactFlowProvider>
    </div>
  );
};


export default MKGVisualizer;